"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import YourBarrioLoader from "@/components/ui/YourBarrioLoader";
import useBusinessProfileAccessGate from "@/components/auth/useBusinessProfileAccessGate";
import { useLocation } from "@/components/location/LocationProvider";
import CustomerMap from "@/components/customer/CustomerMap";
import { getCustomerBusinessUrl } from "@/lib/ids/publicRefs";
import { useSavedBusinesses } from "@/lib/hooks/useSavedBusinesses";
import { getLocationCacheKey } from "@/lib/location";
import { hasCoordinates } from "@/lib/location/filter";
import NearbySplitViewShell from "./_components/NearbySplitViewShell";
import NearbyResultsPane from "./_components/NearbyResultsPane";
import Dropdown from "./_components/Dropdown";
import styles from "./nearby.module.css";

const isSameBusinessList = (prev, next) => {
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    if (prev[i]?.id !== next[i]?.id) return false;
  }
  return true;
};

const normalizeNum = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCategoryToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const NEW_BUSINESS_DAYS = 45;
const INITIAL_VISIBLE_BUSINESSES = 6;

const VERIFIED_STATUSES = new Set(["auto_verified", "manually_verified"]);
const SORT_OPTIONS = [
  { value: "recommended", label: "Recommended" },
  { value: "distance", label: "Nearest" },
  { value: "newest", label: "Newest" },
];

const daysSince = (value) => {
  const time = Date.parse(value || "");
  if (!Number.isFinite(time)) return Number.POSITIVE_INFINITY;
  return (Date.now() - time) / (1000 * 60 * 60 * 24);
};

const timeValue = (value) => {
  const time = Date.parse(value || "");
  return Number.isFinite(time) ? time : 0;
};

const getBusinessQualityScore = (business) => {
  let score = 0;
  if (
    business?.avatar_media_asset_id ||
    business?.avatar_url ||
    business?.logo_url ||
    business?.profile_photo_url
  ) score += 2;
  if (business?.description && String(business.description).trim().length >= 24) score += 2;
  if (business?.website) score += 1;
  if (business?.city && business?.state) score += 1;
  if (business?.coords) score += 1;
  return score;
};

const getSoftRankScore = (business) => {
  const distance = normalizeNum(business?.distance_km ?? business?.distanceKm);
  const distanceScore =
    typeof distance === "number" ? Math.max(0, 1 - Math.min(distance, 25) / 25) : 0;
  const updatedDays = daysSince(business?.updated_at);
  const createdDays = daysSince(business?.created_at);
  const activityScore =
    Number.isFinite(updatedDays) && updatedDays <= 30 ? (30 - updatedDays) / 30 : 0;
  const newScore =
    Number.isFinite(createdDays) && createdDays <= NEW_BUSINESS_DAYS
      ? (NEW_BUSINESS_DAYS - createdDays) / NEW_BUSINESS_DAYS
      : 0;

  return (
    (business?.isVerified ? 100 : 0) +
    getBusinessQualityScore(business) * 8 +
    activityScore * 10 +
    newScore * 7 +
    distanceScore * 4
  );
};

const buildHookLine = (business) => {
  const category = business?.categoryLabel || business?.category || "Local business";
  const city = business?.city || "";
  if (business?.isNew) return "New on YourBarrio";
  if (business?.isVerified) return "Verified local business";
  if (city) return `${category} in ${city}`;
  return `Discover this ${category.toLowerCase()} on YourBarrio`;
};

const getBusinessSelection = (business) => {
  if (!business) return null;
  const lat = normalizeNum(
    business.coords?.lat ?? business.latitude ?? business.lat ?? business.location?.lat
  );
  const lng = normalizeNum(
    business.coords?.lng ?? business.longitude ?? business.lng ?? business.location?.lng
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const id = business.id || business.public_id || null;
  return {
    id,
    lat,
    lng,
  };
};

const getNearbyStorageKey = (locationKey) =>
  locationKey
    ? `yb_customer_nearby_businesses_${locationKey}`
    : "yb_customer_nearby_businesses";

const readNearbyCacheSnapshot = (key) => {
  if (typeof window === "undefined" || !key) {
    return { businesses: [], cached: false };
  }
  try {
    const cached = window.sessionStorage.getItem(key);
    if (cached === null) return { businesses: [], cached: false };
    const parsed = JSON.parse(cached);
    if (Array.isArray(parsed)) return { businesses: parsed, cached: true };
    if (Array.isArray(parsed?.businesses)) {
      return { businesses: parsed.businesses, cached: true };
    }
  } catch {
    /* ignore cache read errors */
  }
  return { businesses: [], cached: false };
};

export default function NearbyBusinessesClient() {
  const router = useRouter();
  const { user, loadingUser } = useAuth();
  const gateBusinessProfileAccess = useBusinessProfileAccessGate();
  const searchParams = useSearchParams();
  const { location, hydrated: locationHydrated, requestGpsLocation } = useLocation();

  const mapEnabled = process.env.NEXT_PUBLIC_HOME_BISECT_MAP !== "0";
  const mapAvailable = mapEnabled && process.env.NEXT_PUBLIC_DISABLE_MAP !== "1";
  const locationKey = getLocationCacheKey(location);
  const storageKey = getNearbyStorageKey(locationKey);
  const initialNearbyCache = { businesses: [], cached: false };

  const [search, setSearch] = useState("");
  const [businessTypeFilter, setBusinessTypeFilter] = useState("All");
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [sortMode, setSortMode] = useState("recommended");
  // This controls the active List/Map view across mobile and desktop breakpoints.
  const [mobileView, setMobileView] = useState("list");
  const [isMobile, setIsMobile] = useState(false);
  const [preciseLocationLoading, setPreciseLocationLoading] = useState(false);
  const [visibleBusinessCount, setVisibleBusinessCount] = useState(INITIAL_VISIBLE_BUSINESSES);

  const [ybBusinesses, setYbBusinesses] = useState(initialNearbyCache.businesses);
  const [ybBusinessesLoading, setYbBusinessesLoading] = useState(
    !initialNearbyCache.cached
  );
  const [ybBusinessesRefreshing, setYbBusinessesRefreshing] = useState(false);
  const [ybBusinessesError, setYbBusinessesError] = useState(null);
  const [hasLoadedYb, setHasLoadedYb] = useState(initialNearbyCache.cached);

  const [hoveredBusinessId, setHoveredBusinessId] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [mapControls, setMapControls] = useState(null);
  const {
    savedBusinessIds,
    savingBusinessIds,
    showSaveControls,
    toggleSavedBusiness,
  } = useSavedBusinesses();

  const cardRefs = useRef(new Map());
  const hasLoadedYbRef = useRef(initialNearbyCache.cached);
  const ybRequestIdRef = useRef(0);
  const selectedBusinessIdRef = useRef(null);

  useEffect(() => {
    const initial = readNearbyCacheSnapshot(storageKey);

    setYbBusinesses(initial.businesses);
    setHasLoadedYb(initial.cached);
    hasLoadedYbRef.current = initial.cached;
    setYbBusinessesLoading(!initial.cached);
    setYbBusinessesRefreshing(false);
    setYbBusinessesError(null);
  }, [storageKey]);

  useEffect(() => {
    hasLoadedYbRef.current = hasLoadedYb;
  }, [hasLoadedYb]);

  useEffect(() => {
    selectedBusinessIdRef.current = selectedBusinessId;
  }, [selectedBusinessId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const applyAvatarUpdate = (payload) => {
      const businessId = String(payload?.businessId || "").trim();
      if (!businessId) return;
      const patch = {
        profile_photo_url: payload.profile_photo_url || "",
        avatar_media_asset_id: payload.avatar_media_asset_id || null,
        business_avatar_media_asset: payload.business_avatar_media_asset || null,
      };
      const matchesBusiness = (business) =>
        String(business?.id || "") === businessId ||
        String(business?.owner_user_id || "") === businessId;
      setYbBusinesses((prev) =>
        prev.map((business) => (matchesBusiness(business) ? { ...business, ...patch } : business))
      );
      setSelectedBusiness((prev) =>
        prev && matchesBusiness(prev) ? { ...prev, ...patch } : prev
      );
    };

    const handleWindowUpdate = (event) => applyAvatarUpdate(event.detail);
    window.addEventListener("yb:business-avatar-updated", handleWindowUpdate);

    let channel = null;
    try {
      channel = new BroadcastChannel("yb-business-avatar");
      channel.onmessage = (event) => applyAvatarUpdate(event.data);
    } catch {}

    return () => {
      window.removeEventListener("yb:business-avatar-updated", handleWindowUpdate);
      if (channel) channel.close();
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.documentElement.setAttribute("data-route", "customer-nearby");
    return () => {
      if (document.documentElement.getAttribute("data-route") === "customer-nearby") {
        document.documentElement.removeAttribute("data-route");
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(max-width: 767px)");
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const urlQuery = (searchParams?.get("q") || "").trim();
    const urlCategory = (searchParams?.get("businessType") || searchParams?.get("category") || "").trim();
    setSearch(urlQuery);
    setBusinessTypeFilter(urlCategory || "All");
  }, [searchParams]);

  useEffect(() => {
    if (!businessTypeOptions.length || businessTypeFilter === "All") return;
    const normalizedFilter = normalizeCategoryToken(businessTypeFilter);
    const matched = businessTypeOptions.find((businessType) =>
      [businessType.id, businessType.slug, businessType.name]
        .map(normalizeCategoryToken)
        .includes(normalizedFilter)
    );
    if (matched && businessTypeFilter !== matched.slug) {
      setBusinessTypeFilter(matched.slug);
    } else if (!matched) {
      setBusinessTypeFilter("All");
    }
  }, [businessTypeOptions, businessTypeFilter]);

  useEffect(() => {
    if (!locationHydrated) return;
    if (!locationKey) {
      setYbBusinesses([]);
      setHasLoadedYb(true);
      hasLoadedYbRef.current = true;
      setYbBusinessesLoading(false);
      setYbBusinessesRefreshing(false);
      setYbBusinessesError(null);
      return;
    }

    let active = true;
    const loadYb = async () => {
      const requestId = ++ybRequestIdRef.current;
      const hasRenderableData = hasLoadedYbRef.current;
      setYbBusinessesLoading(!hasRenderableData);
      setYbBusinessesRefreshing(hasRenderableData);
      setYbBusinessesError(null);

      try {
        let rows = [];
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(new DOMException("Timeout", "AbortError")),
            12000
          );
          const params = new URLSearchParams();
          if (location.city && location.region) {
            params.set("city", location.city);
            params.set("state", location.region);
          }
          if (hasCoordinates(location)) {
            params.set("lat", String(location.lat));
            params.set("lng", String(location.lng));
          }
          const url = params.toString()
            ? `/api/public-businesses?${params.toString()}`
            : "/api/public-businesses";
          const res = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          const payload = await res.json().catch(() => ({}));
          if (res.ok && Array.isArray(payload?.businesses)) {
            rows = payload.businesses;
          }
          const responseBusinessTypes = Array.isArray(payload?.businessTypes)
            ? payload.businessTypes
            : Array.isArray(payload?.categories)
              ? payload.categories
              : [];
          if (res.ok && responseBusinessTypes.length) {
            setBusinessTypeOptions(
              responseBusinessTypes
                .filter((businessType) => businessType?.id && businessType?.name)
                .map((businessType) => ({
                  id: businessType.id,
                  name: businessType.name,
                  slug: businessType.slug || normalizeCategoryToken(businessType.name),
                }))
            );
          } else if (res.ok) {
            setBusinessTypeOptions([]);
          }
        } catch (errApi) {
          if (errApi?.name === "AbortError") {
            if (active && requestId === ybRequestIdRef.current) {
              setYbBusinesses([]);
              setHasLoadedYb(true);
              hasLoadedYbRef.current = true;
              setYbBusinessesError("Still loading businesses. Please refresh to retry.");
              setYbBusinessesLoading(false);
              setYbBusinessesRefreshing(false);
            }
            return;
          }
          console.warn("public-businesses endpoint failed", errApi);
        }

        if (!active || requestId !== ybRequestIdRef.current) return;

        const mapped = rows
          .map((row) => {
            const lat = normalizeNum(row.latitude ?? row.lat ?? row.location_lat);
            const lng = normalizeNum(row.longitude ?? row.lng ?? row.location_lng);
            const hasCoords =
              typeof lat === "number" &&
              typeof lng === "number" &&
              !(lat === 0 && lng === 0);

            return {
              id: row.id,
              public_id: row.public_id || null,
              name: row.business_name || row.name || "Local business",
              category: row.category || "Local business",
              categoryLabel: row.category || "Local business",
              businessTypeId: row.business_type_id || null,
              businessTypeSlug: row.business_type_slug || row.business_type || null,
              businessTypeName: row.business_type_name || row.category || null,
              address: row.city
                ? `${row.address || ""}${row.address ? ", " : ""}${row.city}`
                : row.address || "",
              neighborhood:
                row.neighborhood || row.neighbourhood || row.district || row.area || "",
              city: row.city || "",
              state: row.state || row.state_code || row.region || "",
              zip_code: row.zip_code || row.zip || "",
              description: row.description || row.bio || "",
              website: row.website || "",
              avatar_url: row.avatar_url || "",
              logo_url: row.logo_url || "",
              avatar_media_asset_id: row.avatar_media_asset_id || null,
              business_avatar_media_asset: row.business_avatar_media_asset || null,
              cover_photo_url: row.cover_photo_url || "",
              profile_photo_url: row.profile_photo_url || "",
              verification_status: row.verification_status || "",
              isVerified: VERIFIED_STATUSES.has(row.verification_status),
              created_at: row.created_at || null,
              updated_at: row.updated_at || null,
              rating: normalizeNum(row.rating),
              open_now:
                typeof row.open_now === "boolean"
                  ? row.open_now
                  : typeof row.is_open === "boolean"
                    ? row.is_open
                    : null,
              distance_km: normalizeNum(row.distance_km),
              discovery_rank: normalizeNum(row.discovery_rank),
              source: "supabase_businesses",
              coords: hasCoords ? { lat, lng } : null,
            };
          })
          .map((business) => {
            const isNew = daysSince(business.created_at) <= NEW_BUSINESS_DAYS;
            return {
              ...business,
              isNew,
              hookLine: buildHookLine({ ...business, isNew }),
            };
          })
          .filter(Boolean);

        const next = mapped.length ? mapped : [];
        setYbBusinesses((prev) => (isSameBusinessList(prev, next) ? prev : next));
        setHasLoadedYb(true);
        hasLoadedYbRef.current = true;
        if (!next.length) {
          setYbBusinessesError("No businesses available for this location yet.");
        }

        if (typeof window !== "undefined") {
          try {
            sessionStorage.setItem(
              storageKey,
              JSON.stringify({ businesses: next, loadedAt: Date.now() })
            );
          } catch {
            /* ignore cache errors */
          }
        }
      } catch (err) {
        console.warn("Failed to load nearby businesses", err);
        if (!active || requestId !== ybRequestIdRef.current) return;
        setYbBusinesses((prev) => (isSameBusinessList(prev, []) ? prev : []));
        setHasLoadedYb(true);
        hasLoadedYbRef.current = true;
        setYbBusinessesError("Could not load businesses yet. Please try again.");
      } finally {
        if (active && requestId === ybRequestIdRef.current) {
          setYbBusinessesLoading(false);
          setYbBusinessesRefreshing(false);
        }
      }
    };

    loadYb();

    return () => {
      active = false;
    };
  }, [locationHydrated, locationKey, location, storageKey]);

  const showLocationEmpty = locationHydrated && !locationKey;
  const activeBusinessId = hoveredBusinessId || selectedBusinessId || null;

  const filteredBusinesses = useMemo(() => {
    const q = search.trim().toLowerCase();
    const businessTypeFilterNormalized = normalizeCategoryToken(businessTypeFilter);
    const filtered = ybBusinesses.filter((biz) => {
      if (!biz) return false;
      const category = biz.categoryLabel?.toLowerCase() || biz.category?.toLowerCase() || "";
      const businessTypeTokens = [
        biz.businessTypeId,
        biz.businessTypeSlug,
        biz.businessTypeName,
      ]
        .map(normalizeCategoryToken)
        .filter(Boolean);
      const matchesCategory =
        !businessTypeFilterNormalized ||
        businessTypeFilterNormalized === "all" ||
        businessTypeTokens.includes(businessTypeFilterNormalized);
      if (!matchesCategory) return false;
      if (!q) return true;
      const name = biz.name?.toLowerCase() || "";
      const desc = biz.description?.toLowerCase() || "";
      const address = biz.address?.toLowerCase() || "";
      return (
        name.includes(q) ||
        category.includes(q) ||
        desc.includes(q) ||
        address.includes(q)
      );
    });

    return [...filtered].sort((left, right) => {
      if (sortMode === "distance") {
        const leftDistance = normalizeNum(left.distance_km);
        const rightDistance = normalizeNum(right.distance_km);
        const safeLeft =
          typeof leftDistance === "number" ? leftDistance : Number.POSITIVE_INFINITY;
        const safeRight =
          typeof rightDistance === "number" ? rightDistance : Number.POSITIVE_INFINITY;
        return safeLeft - safeRight;
      }

      if (sortMode === "newest") {
        return timeValue(right.created_at) - timeValue(left.created_at);
      }

      const leftRank = normalizeNum(left.discovery_rank) ?? getSoftRankScore(left);
      const rightRank = normalizeNum(right.discovery_rank) ?? getSoftRankScore(right);
      const rankDelta = rightRank - leftRank;
      if (Math.abs(rankDelta) > 0.001) return rankDelta;
      return timeValue(right.updated_at || right.created_at) - timeValue(left.updated_at || left.created_at);
    });
  }, [search, ybBusinesses, businessTypeFilter, sortMode]);

  const businessesForMap = useMemo(() => filteredBusinesses, [filteredBusinesses]);
  const visibleBusinesses = useMemo(
    () => filteredBusinesses.slice(0, visibleBusinessCount),
    [filteredBusinesses, visibleBusinessCount]
  );
  const hasMoreBusinesses = filteredBusinesses.length > visibleBusinessCount;
  const businessTypeDropdownOptions = useMemo(
    () => [
      { value: "All", label: "All shops" },
      ...businessTypeOptions.map((businessType) => ({
        value: businessType.slug || businessType.id || businessType.name,
        label: businessType.name,
      })),
    ],
    [businessTypeOptions]
  );

  useEffect(() => {
    setVisibleBusinessCount(INITIAL_VISIBLE_BUSINESSES);
  }, [filteredBusinesses]);

  useEffect(() => {
    if (!filteredBusinesses.length) {
      setSelectedBusinessId(null);
      setSelectedBusiness(null);
      setHoveredBusinessId(null);
      return;
    }
    if (selectedBusinessId == null) return;
    const stillExists = filteredBusinesses.some((biz) => biz.id === selectedBusinessId);
    if (!stillExists) {
      setSelectedBusinessId(null);
      setSelectedBusiness(null);
    }
  }, [filteredBusinesses, selectedBusinessId]);

  const registerCard = useCallback((id, node) => {
    if (!id) return;
    if (node) {
      cardRefs.current.set(id, node);
    } else {
      cardRefs.current.delete(id);
    }
  }, []);

  const onCardClick = useCallback(
    (business) => {
      const target = getCustomerBusinessUrl(business);
      if (!gateBusinessProfileAccess(undefined, target)) return;
      router.push(target);
    },
    [gateBusinessProfileAccess, router]
  );

  const onToggleSaveShop = useCallback(
    (business) => toggleSavedBusiness(business, { fallbackPath: "/customer/nearby" }),
    [toggleSavedBusiness]
  );

  const clearSelectedMarker = useCallback((businessId = null) => {
    const currentSelectedBusinessId = selectedBusinessIdRef.current;
    const matchesSelected =
      businessId == null ||
      currentSelectedBusinessId == null ||
      String(currentSelectedBusinessId) === String(businessId);

    if (!matchesSelected) return;

    setSelectedBusinessId(null);
    setSelectedBusiness(null);
    setHoveredBusinessId((current) =>
      businessId == null || String(current) === String(businessId) ? null : current
    );
  }, []);

  const onViewModeChange = useCallback(
    (nextView) => {
      if (nextView === "list") {
        clearSelectedMarker();
      }
      setMobileView(nextView === "map" ? "map" : "list");
    },
    [clearSelectedMarker]
  );

  const onMarkerClick = useCallback((businessId) => {
    if (!businessId) return;
    setSelectedBusinessId(businessId);
    setHoveredBusinessId(businessId);
    const selected = filteredBusinesses.find((biz) => String(biz.id) === String(businessId));
    setSelectedBusiness(getBusinessSelection(selected));
    const node = mobileView === "list" ? cardRefs.current.get(businessId) : null;
    if (node) {
      node.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
    }
  }, [filteredBusinesses, mobileView]);

  const onCardMapFocusClick = useCallback(
    (business) => {
      if (!business?.id) return;
      const selected = getBusinessSelection(business);
      if (!selected) return;
      setSelectedBusinessId(business.id);
      setSelectedBusiness(selected);
      setHoveredBusinessId(business.id);
      setMobileView("map");
    },
    []
  );

  const onPreciseLocationClick = useCallback(async () => {
    setPreciseLocationLoading(true);
    try {
      await requestGpsLocation();
    } finally {
      setPreciseLocationLoading(false);
    }
  }, [requestGpsLocation]);

  useEffect(() => {
    if (mobileView !== "map" || !mapControls?.resize) return undefined;
    let rafId = 0;
    const timerId = window.setTimeout(() => {
      rafId = window.requestAnimationFrame(() => {
        mapControls.resize?.();
      });
    }, 80);
    return () => {
      window.clearTimeout(timerId);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [mapControls, mobileView]);

  const locationLabel = location?.label || [location?.city, location?.region].filter(Boolean).join(", ");
  const usesPreciseLocation = location?.source === "gps";
  const controlClassName =
    "h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-sm font-normal text-slate-950 placeholder:text-slate-400 focus:border-violet-300/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70";
  const controls = (
    <div className="space-y-1">
      <div className="flex flex-col gap-0.5">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-violet-700">
            Discover local businesses
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-0 gap-y-1">
            <h1 className="text-[2rem] font-semibold tracking-[-0.03em] text-slate-950 sm:text-[2.35rem]">
              Explore near {locationLabel || "Long Beach, CA"}
            </h1>
            <span className="text-sm font-normal text-slate-500">
              &nbsp;· {filteredBusinesses.length} businesses
            </span>
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-col gap-2.5 lg:grid lg:grid-cols-[minmax(260px,1fr)_210px_180px_auto_auto] lg:items-center">
        <label className="block w-full min-w-0">
          <span className="sr-only">Search nearby businesses</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, business type, or address"
            data-testid="nearby-search-input"
            className={controlClassName}
          />
        </label>

        <label className="block">
          <span className="sr-only">Filter by business type</span>
          <Dropdown
            value={businessTypeFilter}
            options={businessTypeDropdownOptions}
            onChange={setBusinessTypeFilter}
            ariaLabel="Filter by business type"
            testId="nearby-category-select"
          />
        </label>

        <label className="block">
          <span className="sr-only">Sort businesses</span>
          <Dropdown
            value={sortMode}
            options={SORT_OPTIONS}
            onChange={setSortMode}
            ariaLabel="Sort businesses"
            testId="nearby-sort-select"
          />
        </label>

        <div className="inline-flex h-11 w-fit rounded-lg border border-slate-200 bg-white p-1">
          {[
            { key: "list", label: "List" },
            { key: "map", label: "Map" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onViewModeChange(item.key)}
              className={`rounded-md px-4 text-sm font-medium transition ${
                mobileView === item.key
                  ? "bg-violet-50 text-violet-700"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              aria-pressed={mobileView === item.key}
              data-testid={`nearby-toggle-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {!usesPreciseLocation ? (
          <button
            type="button"
            onClick={onPreciseLocationClick}
            disabled={preciseLocationLoading}
            className="inline-flex h-11 w-fit shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-transparent px-4 text-sm font-medium text-slate-500 transition hover:border-violet-200 hover:text-violet-700 disabled:cursor-wait disabled:opacity-70"
          >
            {preciseLocationLoading ? "Checking location..." : "Improve distance accuracy"}
          </button>
        ) : null}
      </div>
    </div>
  );

  if (loadingUser && !user) {
    return (
      <div className="relative min-h-screen px-6 pt-10 text-slate-950">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-[#faf6f0]" />
        </div>
        <div className="flex min-h-screen items-center justify-center">
          <YourBarrioLoader size="md" className="text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <section
      className={`relative min-h-screen w-full pb-6 pt-0 text-slate-950 ${styles.nearbyPage}`}
      data-testid="nearby-page-root"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#faf6f0]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(250,246,240,0.98))]" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-6 md:px-8 lg:px-10">
        {showLocationEmpty ? (
          <div className="py-6 text-sm text-slate-600">
            Explore local businesses near Long Beach while your location finishes loading.
          </div>
        ) : (
          <NearbySplitViewShell
            mobileView={mobileView}
            onMobileViewChange={onViewModeChange}
            renderMobileToggle={false}
            controls={controls}
            resultsPane={
              <NearbyResultsPane
                businesses={filteredBusinesses}
                visibleBusinesses={visibleBusinesses}
                loading={ybBusinessesLoading && !hasLoadedYb}
                loaded={hasLoadedYb}
                refreshing={ybBusinessesRefreshing && hasLoadedYb}
                error={ybBusinessesError}
                activeBusinessId={activeBusinessId}
                selectedBusinessId={selectedBusinessId}
                onCardHover={setHoveredBusinessId}
                onCardLeave={() => setHoveredBusinessId(null)}
                onCardClick={onCardClick}
                onCardMapFocusClick={onCardMapFocusClick}
                onToggleSaveShop={onToggleSaveShop}
                savedBusinessIds={savedBusinessIds}
                savingBusinessIds={savingBusinessIds}
                showSaveControls={showSaveControls}
                isMobile={isMobile}
                registerCard={registerCard}
                hasMoreBusinesses={hasMoreBusinesses}
                onLoadMore={() =>
                  setVisibleBusinessCount((prev) =>
                    Math.min(prev + INITIAL_VISIBLE_BUSINESSES, filteredBusinesses.length)
                  )
                }
                onResetFilters={() => {
                  setSearch("");
                  setBusinessTypeFilter("All");
                }}
              />
            }
            mapPane={
              <div
                className="relative h-[60vh] min-h-[360px] md:h-full md:min-h-0"
                data-testid="nearby-map-canvas"
              >
                <CustomerMap
                  mapEnabled={mapAvailable}
                  mapBusinesses={businessesForMap}
                  enableSearch={false}
                  preferredCenter={
                    Number.isFinite(location.lat) && Number.isFinite(location.lng)
                      ? { lat: location.lat, lng: location.lng }
                      : null
                  }
                  onControlsReady={setMapControls}
                  activeBusinessId={activeBusinessId}
                  hoveredBusinessId={hoveredBusinessId}
                  selectedBusiness={selectedBusiness}
                  selectedBusinessId={selectedBusinessId}
                  markerClickBehavior="select"
                  onMarkerHover={setHoveredBusinessId}
                  onMarkerLeave={() => setHoveredBusinessId(null)}
                  onMarkerClick={onMarkerClick}
                  onMarkerClear={clearSelectedMarker}
                  showRecenterControl
                  />
              </div>
            }
          />
        )}
      </div>
    </section>
  );
}
